import React, { useEffect, useMemo, useState } from 'react';
import { pf2eApi, type PF2eSourceBook } from '../../api/client';
import { useLocalization, useTranslation } from '../../i18n/provider';
import { canonicalizeSourceBook, formatSourceWithPage } from '../../utils/sourceBookTranslator';
import { findSourceBook, localizedSourceBookName } from '../../utils/sourceBookDisplay';
import OrcRichText from '../OrcRichText';
import './SourceBookCard.css';

type SourceBookCardProps = {
  sourceBook: string | null | undefined;
  sourcePg?: string | number | null;
  className?: string;
  showLicense?: boolean;
};

const SOURCE_BOOK_CACHE = new Map<string, PF2eSourceBook[]>();
const SOURCE_BOOK_PROMISES = new Map<string, Promise<PF2eSourceBook[]>>();

function loadSourceBooks(locale: string): Promise<PF2eSourceBook[]> {
  const key = locale || 'en';
  const cached = SOURCE_BOOK_CACHE.get(key);
  if (cached) return Promise.resolve(cached);

  const existing = SOURCE_BOOK_PROMISES.get(key);
  if (existing) return existing;

  const promise = pf2eApi.getSourceBooks({ locale: key })
    .then(response => response.data.source_books || [])
    .then(books => {
      SOURCE_BOOK_CACHE.set(key, books);
      SOURCE_BOOK_PROMISES.delete(key);
      return books;
    })
    .catch(error => {
      SOURCE_BOOK_PROMISES.delete(key);
      throw error;
    });

  SOURCE_BOOK_PROMISES.set(key, promise);
  return promise;
}

function displayDate(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

export default function SourceBookCard({ sourceBook, sourcePg, className, showLicense = true }: SourceBookCardProps) {
  const t = useTranslation();
  const { language } = useLocalization();
  const [books, setBooks] = useState<PF2eSourceBook[]>(() => SOURCE_BOOK_CACHE.get(language) || []);
  const [isOpen, setIsOpen] = useState(false);
  const canonicalSource = canonicalizeSourceBook(sourceBook);

  useEffect(() => {
    if (!canonicalSource) return;
    let cancelled = false;
    loadSourceBooks(language)
      .then(nextBooks => {
        if (!cancelled) setBooks(nextBooks);
      })
      .catch(() => {
        if (!cancelled) setBooks([]);
      });
    return () => { cancelled = true; };
  }, [canonicalSource, language]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const book = useMemo(() => findSourceBook(books, sourceBook), [books, sourceBook]);
  const title = localizedSourceBookName(book, sourceBook, language);
  const fallbackLabel = formatSourceWithPage(sourceBook, sourcePg, t);
  const page = sourcePg !== null && sourcePg !== undefined && sourcePg !== ''
    ? `${t('character_creation.pageIndicator', {}, 'pg.')} ${sourcePg}`
    : '';
  const description = (language !== 'en' && book?.localized_description) || book?.description || '';
  const releaseDate = displayDate(book?.release_date);
  const cardClassName = ['source-book-card', className || ''].filter(Boolean).join(' ');

  if (!canonicalSource && !page) return null;

  return (
    <>
      <button
        type="button"
        className={cardClassName}
        onClick={() => setIsOpen(true)}
        aria-label={t('character_creation.sourceBook.openDetails', {}, 'Open source details')}
      >
        <div className="source-book-card-main">
          <div className="step2-value-card-label">{t('character_creation.source')}</div>
          <div className="source-book-card-title">{title || fallbackLabel}</div>
          {page && <div className="source-book-card-page">{page}</div>}
        </div>
        {book?.cover_path && (
          <img className="source-book-card-cover" src={book.cover_path} alt="" loading="lazy" />
        )}
      </button>

      {isOpen && (
        <div
          className="source-book-modal-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="source-book-modal-title"
        >
          <div className="source-book-modal" onClick={(event) => event.stopPropagation()}>
            <div className="source-book-modal-header">
              <div>
                <div className="source-book-modal-kicker">{t('character_creation.source')}</div>
                <h3 id="source-book-modal-title" className="source-book-modal-title">{title || fallbackLabel}</h3>
              </div>
              <button
                type="button"
                className="source-book-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label={t('common.close', {}, 'Close')}
              >
                ×
              </button>
            </div>

            <div className="source-book-modal-body">
              <div className="source-book-modal-cover-frame">
                {book?.cover_path ? (
                  <img className="source-book-modal-cover" src={book.cover_path} alt="" />
                ) : (
                  <div className="source-book-modal-cover-placeholder" aria-hidden="true">PF2e</div>
                )}
              </div>

              <div className="source-book-modal-content">
                <div className="source-book-modal-meta">
                  {page && (
                    <div>
                      <span>{t('character_creation.pageIndicator', {}, 'pg.')}</span>
                      <strong>{sourcePg}</strong>
                    </div>
                  )}
                  {releaseDate && (
                    <div>
                      <span>{t('character_creation.sourceBook.releaseDate', {}, 'Release Date')}</span>
                      <strong>{releaseDate}</strong>
                    </div>
                  )}
                  {showLicense && book?.license && (
                    <div>
                      <span>{t('character_creation.sourceBook.license', {}, 'License')}</span>
                      <strong>{book.license}</strong>
                    </div>
                  )}
                  {book?.remastered && (
                    <div>
                      <span>{t('character_creation.sourceBook.remastered', {}, 'Remastered')}</span>
                      <strong>{t('common.yes', {}, 'Yes')}</strong>
                    </div>
                  )}
                </div>

                <div className="source-book-modal-description">
                  {description ? (
                    <OrcRichText content={description} html={description.includes('<')} markdown={!description.includes('<')} />
                  ) : (
                    <p>{t('character_creation.sourceBook.noDescription', {}, 'No description available.')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
